import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const SECTIONS = [
  {
    icon: 'ri-rocket-2-line',
    title: 'Premiers pas',
    items: [
      { label: 'Créer votre compte marchand', text: 'Inscrivez-vous, choisissez votre activité puis validez votre adresse e-mail. Votre espace est prêt en moins de deux minutes.' },
      { label: 'Choisir et publier un thème', text: 'Depuis Theme Store, sélectionnez un thème adapté à votre métier. La prévisualisation est immédiate, la publication en un clic.' },
      { label: 'Ajouter vos premiers produits', text: 'Produits, services, hébergements ou formations : chaque catalogue a son propre formulaire, avec images, prix et variantes.' },
    ],
  },
  {
    icon: 'ri-shopping-bag-3-line',
    title: 'Vendre au quotidien',
    items: [
      { label: 'Commandes et paiements', text: 'Suivez chaque commande depuis le tableau de bord. À la validation du paiement, la facture est générée automatiquement.' },
      { label: 'Caisse et stock', text: 'Encaissez en boutique, scannez les QR codes clients et laissez le stock se décrémenter à chaque vente.' },
      { label: 'Carnet de crédit', text: 'Enregistrez les dettes de vos clients, les paiements partiels et les relevés imprimables.' },
    ],
  },
  {
    icon: 'ri-file-text-line',
    title: 'Facturation & conformité',
    items: [
      { label: 'Renseigner vos identifiants', text: 'ICE (15 chiffres), IF, RC et TP se saisissent une seule fois dans Finance → Paramètres. Ils apparaissent ensuite sur tous vos documents.' },
      { label: 'TVA', text: 'La TVA n’est appliquée que si vous avez activé « Assujetti à la TVA ». Sinon, la mention d’exonération est utilisée.' },
      { label: 'Plafond de règlement en espèces', text: 'Un règlement fournisseur en espèces est bloqué au-delà de 5 000 DH par jour et par fournisseur. Privilégiez le virement ou le chèque barré.' },
    ],
  },
  {
    icon: 'ri-team-line',
    title: 'Équipe & accès',
    items: [
      { label: 'Inviter des collaborateurs', text: 'Attribuez des rôles précis : caisse, gestion de stock, facturation, support client.' },
      { label: 'Journal d’activité', text: 'Chaque action sensible est horodatée avec son auteur, pour une traçabilité complète.' },
    ],
  },
];

const FAQ = [
  {
    q: 'Ai-je besoin d’un nom de domaine ?',
    a: 'Non. Votre site est accessible immédiatement sur une adresse Zifek. Vous pouvez brancher un domaine personnalisé plus tard, sans rien perdre.',
  },
  {
    q: 'Mes données sont-elles isolées des autres commerces ?',
    a: 'Oui. Chaque donnée est rattachée à votre commerce. Aucun autre marchand ne peut y accéder.',
  },
  {
    q: 'Puis-je exporter mes données ?',
    a: 'Tous les modules disposent d’un export CSV ou Excel, et vos documents sont téléchargeables en PDF.',
  },
  {
    q: 'La facturation est-elle conforme à la réglementation marocaine ?',
    a: 'Zifek vous aide à respecter les mentions obligatoires (ICE, IF, RC, TP, numérotation continue). Il ne remplace pas un logiciel de comptabilité certifié : faites valider vos pratiques par votre comptable.',
  },
];

export default function DocumentationPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar forceScrolled />

      <header className="pt-24 md:pt-32 pb-10 px-4 md:px-6 lg:px-10 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
            <i className="ri-book-open-line"></i>Documentation
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold font-heading text-foreground-950 leading-tight max-w-3xl">
            Le guide complet de Zifek
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl">
            De la création de votre boutique à l’émission de vos factures : tout est expliqué pas à pas.
          </p>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-10 py-10 md:py-14">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
          <aside className="lg:sticky lg:top-24 h-fit">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400 mb-3">Sommaire</p>
            <nav className="flex flex-col gap-1">
              {SECTIONS.map((s, i) => (
                <a
                  key={s.title}
                  href={`#section-${i}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 transition-colors no-underline"
                >
                  <i className={`${s.icon} text-primary-500`}></i>
                  {s.title}
                </a>
              ))}
              <a
                href="#faq"
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 transition-colors no-underline"
              >
                <i className="ri-question-answer-line text-accent-600"></i>
                Questions fréquentes
              </a>
            </nav>
          </aside>

          <div className="space-y-8 min-w-0">
            {SECTIONS.map((s, i) => (
              <section key={s.title} id={`section-${i}`} className="scroll-mt-24">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 flex items-center gap-2 mb-4">
                  <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                    <i className={`${s.icon} text-sm`}></i>
                  </span>
                  {s.title}
                </h2>
                <div className="space-y-3">
                  {s.items.map((item) => (
                    <article key={item.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">{item.label}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.text}</p>
                    </article>
                  ))}
                </div>
              </section>
            ))}

            <section id="faq" className="scroll-mt-24">
              <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 flex items-center gap-2 mb-4">
                <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-question-answer-line text-sm"></i>
                </span>
                Questions fréquentes
              </h2>
              <div className="divide-y divide-background-200/70 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                {FAQ.map((f) => (
                  <details key={f.q} className="group">
                    <summary className="flex items-center justify-between gap-3 px-4 md:px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-900">{f.q}</span>
                      <i className="ri-add-line text-foreground-400 group-open:rotate-45 transition-transform"></i>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 -mt-1 text-sm text-foreground-600 leading-relaxed">{f.a}</div>
                  </details>
                ))}
              </div>
            </section>

            <div className="bg-foreground-950 rounded-lg p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold font-heading text-background-50">Besoin d’un coup de main ?</h3>
                <p className="text-sm text-background-50/60 mt-1">Notre équipe répond aux questions techniques et commerciales.</p>
              </div>
              <Link
                to="/dashboard/apps/customer-support"
                className="px-5 py-2.5 rounded-full bg-background-50 text-foreground-950 text-sm font-semibold whitespace-nowrap no-underline hover:bg-background-100 transition-colors"
              >
                Contacter le support
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}